/**
 * The event schedule: the single source for every time the site shows.
 *
 * Instants carry the `-04:00` offset because October 23, 2026 falls under
 * Eastern Daylight Time (daylight saving ends November 1). Display formatting
 * names `America/New_York` explicitly, so a visitor in another zone still sees
 * the event's own clock.
 */

export const EVENT_TIME_ZONE = 'America/New_York'

export type Block = {
  id: string
  start: string
  end: string
  title: string
  detail?: string
}

export const SCHEDULE: readonly Block[] = [
  {
    id: 'welcome',
    start: '2026-10-23T11:30:00-04:00',
    end: '2026-10-23T12:00:00-04:00',
    title: 'Meet-and-greet and dot orientation',
    detail: 'With lunch',
  },
  {
    id: 'kickoff',
    start: '2026-10-23T12:00:00-04:00',
    end: '2026-10-23T12:30:00-04:00',
    title: 'Kickoff, challenge briefing, and team setup',
  },
  {
    id: 'build',
    start: '2026-10-23T12:30:00-04:00',
    end: '2026-10-23T15:30:00-04:00',
    title: 'Build time',
    detail: 'All development through dots, in two-person teams',
  },
  {
    id: 'demos',
    start: '2026-10-23T15:30:00-04:00',
    end: '2026-10-23T16:30:00-04:00',
    title: 'Team presentations and demos',
    detail: 'With drinks and desserts',
  },
  {
    id: 'wrap',
    start: '2026-10-23T16:30:00-04:00',
    end: '2026-10-23T17:00:00-04:00',
    title: 'Judging, prizes, and wrap-up',
  },
]

export type ScheduleStatus =
  | { phase: 'before'; next: Block; msUntilNext: number }
  | { phase: 'active'; current: Block; next: Block | null; msUntilEnd: number }
  | { phase: 'after' }

const ms = (iso: string) => Date.parse(iso)

/**
 * Where `now` falls in the schedule. A block owns its start instant and not
 * its end, so at 12:00 exactly the kickoff is current, not the welcome.
 */
export function scheduleStatus(now: Date, schedule: readonly Block[] = SCHEDULE): ScheduleStatus {
  const t = now.getTime()
  const first = schedule[0]
  if (t < ms(first.start)) {
    return { phase: 'before', next: first, msUntilNext: ms(first.start) - t }
  }
  const index = schedule.findIndex((block) => t >= ms(block.start) && t < ms(block.end))
  if (index === -1) return { phase: 'after' }
  const current = schedule[index]
  return {
    phase: 'active',
    current,
    next: schedule[index + 1] ?? null,
    msUntilEnd: ms(current.end) - t,
  }
}

const timeFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: EVENT_TIME_ZONE,
  hour: 'numeric',
  minute: '2-digit',
})

const dateFormat = new Intl.DateTimeFormat('en-US', {
  timeZone: EVENT_TIME_ZONE,
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
})

export const formatTime = (iso: string) => timeFormat.format(new Date(iso))

export const formatRange = (block: Block) => `${formatTime(block.start)} - ${formatTime(block.end)}`

/** "Friday, October 23, 2026", from the first block so it cannot drift. */
export const EVENT_DATE_LABEL = dateFormat.format(new Date(SCHEDULE[0].start))

/** "11:30 AM - 5:00 PM", the whole program. */
export const EVENT_HOURS_LABEL = `${formatTime(SCHEDULE[0].start)} - ${formatTime(SCHEDULE[SCHEDULE.length - 1].end)}`

/**
 * A countdown that reads the same aloud as it does on screen: "2h 05m 09s",
 * dropping leading units once they are zero. Days appear only before the
 * event week.
 */
export function formatCountdown(remainingMs: number): string {
  const total = Math.max(0, Math.ceil(remainingMs / 1000))
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  if (days > 0) return `${days}d ${hours}h ${pad(minutes)}m`
  if (hours > 0) return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`
  return `${minutes}m ${pad(seconds)}s`
}
